import type { Product } from '~/models/models';

import React from 'react';
import { Alert } from 'Components/nui';
import * as Data from 'Components/Data';
import * as R from 'ramda';
import * as dayjs from 'dayjs';
import FNumber from 'Components/FNumber';
import { getDecimalCount } from '~/utils';
import { FormattedMessage } from 'react-intl';

interface ILoading {
  id: string;
  qty: number;
}

interface IProduct extends Product {
  loading?: ILoading[];
  step: number;
  currency?: { code: string };
  loadingunit: { desc: string };
  index?: {
    step: number;
    type: 'Modifier' | 'Multiplier';
    name: string;
    category: string;
    period: 'Day' | 'Week' | 'Month';
    offset: 'Prior to' | 'Of' | 'After';
  };
}

const getLoading = (product: IProduct, loading?: string) =>
  R.find(R.propEq('id', loading), product?.loading || []);

interface IVariant {
  id: string;
  product: string;
  price?: number | null;
  loading?: string;
  index: number;
}

interface IFields {
  price?: number;
  startprice?: number;
  indexedprice?: 'true' | 'false';
  openprice?: 'true' | 'false';
  indexdate?: dayjs.Dayjs;
  volume?: number;
  totaldeliveries?: number;
  deliveryfrequency?: string;
  frometd?: dayjs.Dayjs;
  loading?: string;
  counterparty?: string | string[];
  counterpartyMode?: 'public' | 'single_private' | 'multiple_private';
  counterpartyChoices?: Array<{ key: string; value: string; label: string }>;
  variants?: Record<string, IVariant>;
}

interface IFormTotals {
  product: IProduct;
  isTender: boolean;
  fields: IFields;
}
export const FormTotals = (props: IFormTotals) => {
  const { product, isTender, fields = {} } = props;
  const {
    volume = 0,
    totaldeliveries = 1,
    deliveryfrequency,
    frometd,
    price,
    startprice,
  } = fields;
  const indexedprice = fields.indexedprice === 'true';
  const openprice = fields.openprice === 'true';
  const loading = getLoading(product, fields.loading);
  const date = fields.indexdate?.clone();

  const pricestep =
    product.index && indexedprice
      ? getDecimalCount(product.index.step)
      : getDecimalCount(product.step);

  const index = product.index;
  const pricetotal = price ?? startprice;
  const operator =
    index && indexedprice ? (
      index.type === 'Multiplier' ? (
        <>&times;</>
      ) : R.isNil(pricetotal) ? (
        <>&plusmn;</>
      ) : pricetotal >= 0 ? (
        <>&#43;</>
      ) : (
        <>&minus;</>
      )
    ) : (
      <></>
    );

  const totalvolume = volume * totaldeliveries;
  const totalvalue =
    indexedprice || !loading?.id
      ? 0
      : product._getValue(price, totalvolume, loading.id);

  const list = (
    <Data.MaybeList>
      {indexedprice && index && (
        <Data.Item
          title={
            <FormattedMessage
              id="orderform-alert-summary-index-label"
              description="Label for index label on Summary alert on new order form"
              defaultMessage="Index"
            />
          }
        >
          <Data.IndexSummary index={index} date={date} />
        </Data.Item>
      )}
      {isTender ? (
        <Data.Item
          title={
            <FormattedMessage
              id="orderform-alert-summary-start-price-label"
              description="Label for Start price label on Summary alert on new order form"
              defaultMessage="Start price"
            />
          }
        >
          {operator}
          {R.isNil(startprice) ? (
            <>Any</>
          ) : (
            <FNumber value={Math.abs(startprice)} decimalCount={pricestep} />
          )}{' '}
          <PriceUnit
            product={product}
            indexedprice={indexedprice}
            loading={loading}
          />
        </Data.Item>
      ) : (
        (!R.isNil(price) || openprice) && (
          <Data.Item
            title={
              <FormattedMessage
                id="orderform-alert-summary-price-label"
                description="Label for Price label on Summary alert on new order form"
                defaultMessage="Price"
              />
            }
          >
            {openprice ? (
              <FormattedMessage
                id="orderform-alert-summary-open-price"
                defaultMessage="Open price"
              />
            ) : (
              <>
                {operator}
                <FNumber
                  value={Math.abs(price!)}
                  decimalCount={pricestep}
                />{' '}
                <PriceUnit
                  product={product}
                  indexedprice={indexedprice}
                  loading={loading}
                />
              </>
            )}
          </Data.Item>
        )
      )}
      {totaldeliveries > 1 && volume > 0 && !!loading && (
        <>
          <Data.Item
            title={
              <FormattedMessage
                id="orderform-alert-summary-deliveries-label"
                description="Label for Deliveries label on Summary alert on new order form"
                defaultMessage="Deliveries"
              />
            }
          >
            {totaldeliveries}&times;
            <FNumber
              value={product._getVolume(volume)}
              decimalCount={getDecimalCount(loading.qty)}
            />{' '}
            <span className="unit">{product._volumeUnit}</span>
            {deliveryfrequency && <> ({deliveryfrequency})</>}
          </Data.Item>
          {frometd && (
            <Data.Item
              title={
                <FormattedMessage
                  id="orderform-alert-summary-deliveries-from-label"
                  description="Label for Deliveries from label on Summary alert on new order form"
                  defaultMessage="Deliveries from"
                />
              }
            >
              {frometd.format('LL')}
            </Data.Item>
          )}
        </>
      )}
      {totalvolume > 0 && !!loading && (
        <Data.Item
          title={
            <FormattedMessage
              id="orderform-alert-summary-total-volume-label"
              description="Label for Total volume label on Summary alert on new order form"
              defaultMessage="Total volume"
            />
          }
        >
          <FNumber
            value={totalvolume}
            decimalCount={getDecimalCount(loading.qty)}
          />{' '}
          <span className="unit">{product._volumeUnit}</span>
        </Data.Item>
      )}
      {totalvalue > 0 && (
        <Data.Item
          title={
            <FormattedMessage
              id="orderform-alert-summary-total-value-label"
              description="Label for Total value label on Summary alert on new order form"
              defaultMessage="Total value"
            />
          }
        >
          <FNumber value={totalvalue} decimalCount={2} />{' '}
          <span className="unit">{product.currency?.code}</span>
        </Data.Item>
      )}
      {((fields.counterpartyMode && fields.counterpartyMode !== 'public') ||
        (fields.counterpartyMode === 'public' &&
          fields.counterpartyChoices &&
          fields.counterpartyChoices.length > 0) ||
        (fields.counterparty &&
          (Array.isArray(fields.counterparty)
            ? fields.counterparty.length > 0
            : true))) &&
        fields.counterpartyChoices && (
          <Data.Item
            title={
              <FormattedMessage
                id="orderform-alert-summary-counterparties-label"
                description="Label for Counterparties label on Summary alert on new order form"
                defaultMessage="Counterparties"
              />
            }
          >
            {fields.counterpartyMode === 'public' ? (
              fields.counterpartyChoices.length
            ) : (
              <>
                {Array.isArray(fields.counterparty)
                  ? fields.counterparty.length
                  : fields.counterparty
                    ? 1
                    : 0}
                /{fields.counterpartyChoices.length}
              </>
            )}
          </Data.Item>
        )}
      {(() => {
        const variantsCount = fields.variants
          ? Object.values(fields.variants).filter((v) => v.product?.length > 0)
              .length
          : 0;
        return (
          variantsCount > 0 && (
            <Data.Item
              title={
                <FormattedMessage
                  id="orderform-alert-summary-variants-label"
                  description="Label for Variants label on Summary alert on new order form"
                  defaultMessage="Variants"
                />
              }
            >
              {variantsCount}
            </Data.Item>
          )
        );
      })()}
    </Data.MaybeList>
  );

  // dont't render if no item to display
  const validChildren = list.props.children.filter(Boolean);
  if (validChildren.length === 0) return null;

  return (
    <div className="ant-row ant-form-item mb-20">
      <Alert hasicon type="info" className="order-pdv-summary">
        <h4>
          <FormattedMessage
            id="orderform-alert-summary-title"
            description="Title for Summary alert on new order form"
            defaultMessage="Summary"
          />
        </h4>
        {list}
      </Alert>
    </div>
  );
};

interface IPriceUnit {
  product: IProduct;
  loading?: ILoading;
  indexedprice: boolean;
}
const PriceUnit = ({ product, indexedprice, loading }: IPriceUnit) =>
  loading && !indexedprice ? (
    <span className="unit">{product.getDesc(loading.id, undefined)}</span>
  ) : (
    <></>
  );
